import { useState, useMemo } from 'react';
import { Container, Typography, Box } from '@mui/material';
import BasePage from '../BasePage';
import AdminLayout from '../../components/AdminLayout';
import ContentTabs, { ContentTab } from '../../components/admin/content/ContentTabs';
import HomesModeration from '../../components/admin/content/HomesModeration';
import ChatModeration from '../../components/admin/content/ChatModeration';
import BugReportsModeration from '../../components/admin/content/BugReportsModeration';
import ReferralsModeration from '../../components/admin/content/ReferralsModeration';
import { useAdminContent } from '../../hooks/admin/useAdminContent';

/**
 * AdminContent - Content moderation page
 *
 * Section 4: Content Moderation
 * - Homes moderation (flag/unflag, delete)
 * - Chat messages moderation
 * - Bug reports queue
 * - Referrals management
 *
 * Features:
 * - Tabbed interface for 4 content types
 * - Search and filtering per tab
 * - Flag/review/approve workflows
 * - Preview modals for detailed review
 * - Severity indicators for bug reports
 * - Badge counts for flagged/pending items
 */
export default function AdminContent() {
  const [activeTab, setActiveTab] = useState<ContentTab>('homes');

  const {
    // Data
    homes,
    conversations,
    bugReports,
    referrals,

    // State
    isLoading,
    error,

    // Filters
    setHomesFilters,
    setConversationsFilters,
    setBugReportsFilters,
    setReferralsFilters,

    // Home actions
    flagHome,
    unflagHome,
    deleteHome,
    getHomeDetail,

    // Chat actions
    getConversationMessages,
    flagConversation,

    // Bug report actions
    updateBugReportStatus,
    assignBugReport,

    // Referral actions
    approveReferral,
    rejectReferral,
  } = useAdminContent();

  // Calculate badge counts
  const counts = useMemo(() => {
    const flaggedHomes = homes.filter((h) => h.flagged).length;
    const flaggedChats = conversations.filter((c) => c.flagged).length;
    const newBugs = bugReports.filter((r) => r.status === 'new').length;
    const pendingReferrals = referrals.filter((r) => r.status === 'pending').length;

    return {
      flaggedHomes,
      flaggedChats,
      newBugs,
      pendingReferrals,
    };
  }, [homes, conversations, bugReports, referrals]);

  // Filter handlers
  const handleHomesSearch = (query: string) => {
    setHomesFilters((prev) => ({ ...prev, search: query }));
  };

  const handleHomesFilterStatus = (status: string) => {
    setHomesFilters((prev) => ({ ...prev, status }));
  };

  const handleHomesFilterFlagged = (flagged: boolean | undefined) => {
    setHomesFilters((prev) => ({ ...prev, flagged }));
  };

  const handleConversationsSearch = (query: string) => {
    setConversationsFilters((prev) => ({ ...prev, search: query }));
  };

  const handleConversationsFilterFlagged = (flagged: boolean | undefined) => {
    setConversationsFilters((prev) => ({ ...prev, flagged }));
  };

  const handleBugReportsSearch = (query: string) => {
    setBugReportsFilters((prev) => ({ ...prev, search: query }));
  };

  const handleBugReportsFilterStatus = (status: string) => {
    setBugReportsFilters((prev) => ({ ...prev, status }));
  };

  const handleBugReportsFilterSeverity = (severity: string) => {
    setBugReportsFilters((prev) => ({
      ...prev,
      severity: severity as 'low' | 'medium' | 'high' | 'critical' | undefined,
    }));
  };

  const handleBugReportsFilterCategory = (category: string) => {
    setBugReportsFilters((prev) => ({ ...prev, category }));
  };

  const handleReferralsSearch = (query: string) => {
    setReferralsFilters((prev) => ({ ...prev, search: query }));
  };

  const handleReferralsFilterStatus = (status: string) => {
    setReferralsFilters((prev) => ({ ...prev, status }));
  };

  return (
    <BasePage
      title="Content Moderation | CribCritic Admin"
      description="Moderate content"
    >
      <AdminLayout>
        <Container maxWidth="xl">
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Content Moderation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and moderate user-generated content across the platform
            </Typography>
          </Box>

          {error && (
            <Box sx={{ mb: 3 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <ContentTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            counts={counts}
          />

          {/* Homes Tab */}
          {activeTab === 'homes' && (
            <HomesModeration
              homes={homes}
              isLoading={isLoading}
              onSearch={handleHomesSearch}
              onFilterStatus={handleHomesFilterStatus}
              onFilterFlagged={handleHomesFilterFlagged}
              onFlagHome={flagHome}
              onUnflagHome={unflagHome}
              onDeleteHome={deleteHome}
              onViewHomeDetail={getHomeDetail}
            />
          )}

          {/* Chat Messages Tab */}
          {activeTab === 'chats' && (
            <ChatModeration
              conversations={conversations}
              isLoading={isLoading}
              onSearch={handleConversationsSearch}
              onFilterFlagged={handleConversationsFilterFlagged}
              onFlagConversation={flagConversation}
              onGetConversationMessages={getConversationMessages}
            />
          )}

          {/* Bug Reports Tab */}
          {activeTab === 'bugs' && (
            <BugReportsModeration
              bugReports={bugReports}
              isLoading={isLoading}
              onSearch={handleBugReportsSearch}
              onFilterStatus={handleBugReportsFilterStatus}
              onFilterSeverity={handleBugReportsFilterSeverity}
              onFilterCategory={handleBugReportsFilterCategory}
              onUpdateStatus={updateBugReportStatus}
              onAssign={assignBugReport}
            />
          )}

          {/* Referrals Tab */}
          {activeTab === 'referrals' && (
            <ReferralsModeration
              referrals={referrals}
              isLoading={isLoading}
              onSearch={handleReferralsSearch}
              onFilterStatus={handleReferralsFilterStatus}
              onApprove={approveReferral}
              onReject={rejectReferral}
            />
          )}
        </Container>
      </AdminLayout>
    </BasePage>
  );
}
